import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import dayjs from "dayjs";
import API from "../../../../api";
import CircularIndeterminate from "../../../../components/circularProgress";
import { CardContent, Grid, keyframes, IconButton, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import DehazeIcon from "@mui/icons-material/Dehaze";
import {
  TOEstablishTheColorOfLog,
  renderTypeCell,
} from "../../../../components/ImportantFunctions";
import CloseIcon from "@mui/icons-material/Close";
import StyledCard from "../../../../components/StyledCard";

const borderColorChange = keyframes`
  0% {
    border-color: #43B4B9;
  }
  33% {
    border-color: #596035;
  }
  66% {
    border-color: #562984;
  }
  100% {
    border-color: #43B4B9;
  }
`;

const NetworkTrafficChart = () => {
  const [chartData, setChartData] = useState({
    series: [],
    options: {},
  });
  const [dataAnomalies, setDataAnomalies] = useState([]);
  const [dataNormal, setDataNormal] = useState([]);
  const [charged, setCharged] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [filteredTime, setFilteredTime] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingFiltered, setLoadingFiltered] = useState(true);

  useEffect(() => {
    API.getTiresiaNetwork().then((res) => {
      const transformedData =
  res.data.anomalies?.map((item, index) => ({
    id: index,
    ...item,
  })) || [];
      setDataAnomalies(res.data.anomalies);
      setTableData(transformedData);
      setDataNormal(res.data.normal);
      setCharged(true);
    });
  }, []);

  useEffect(() => {
    if (filteredTime) {
      setLoadingFiltered(true);
      API.getTiresiaNetwork({ timeStamp: filteredTime }).then((res) => {
        const transformedData =
          res.data &&
          res.data.map((item, index) => ({
            id: index,
            dettaglio: item._id.dettaglio,
            count: item.count,
            tipo: item._id.tipo,
          }));
        setLoadingFiltered(false);
        setFilteredData(transformedData);
      });
    }
  }, [filteredTime]);

  useEffect(() => {
    if (
      dataAnomalies &&
      dataAnomalies.length > 0 &&
      dataNormal &&
      dataNormal.length > 0
    ) {
      try {
        // Funzione helper per validare e pulire i dati
        const cleanData = (data) => {
          return data
            .filter(
              (item) =>
                item &&
                item.time &&
                (item.bps !== undefined || item.pps !== undefined)
            )
            .map((item) => ({
              time: new Date(item.time).getTime(),
              bps: parseFloat(item.bps) || 0,
              pps: parseFloat(item.pps) || 0,
            }))
            .filter(
              (item) =>
                !isNaN(item.time) && !isNaN(item.bps) && !isNaN(item.pps)
            )
            .sort((a, b) => a.time - b.time);
        };

        // Pulisci e ordina i dati
        const cleanedAnomalies = cleanData(dataAnomalies);
        const cleanedNormal = cleanData(dataNormal);

        if (cleanedAnomalies.length === 0 || cleanedNormal.length === 0) {
          console.error("[NetworkTrafficChart] Dati insufficienti dopo la pulizia");
          return;
        }

        // Prepara dati anomalie
        const anomaliesBpsData = cleanedAnomalies.map((item) => [
          item.time,
          item.bps,
        ]);
        const anomaliesPpsData = cleanedAnomalies.map((item) => [
          item.time,
          item.pps,
        ]);

        // Prepara dati normali
        const normalBpsData = cleanedNormal.map((item) => [
          item.time,
          item.bps,
        ]);
        const normalPpsData = cleanedNormal.map((item) => [
          item.time,
          item.pps,
        ]);

        const series = [
          {
            name: "Normal BPS Range",
            data: normalBpsData,
            type: "area",
          },
          {
            name: "Normal PPS Range",
            data: normalPpsData,
            type: "area",
          },
          {
            name: "BPS (Bytes per Second)",
            data: anomaliesBpsData,
            type: "line",
          },
          {
            name: "PPS (Packets per Second)",
            data: anomaliesPpsData,
            type: "line",
          },
        ];

        const options = {
          chart: {
            type: "line",
            height: 400,
            background: "transparent",
            toolbar: {
              show: true,
              tools: {
                download: false,
                zoom: true,
                reset: true,
                pan: true,
              },
            },
            zoom: {
              enabled: true,
              type: "x",
              autoScaleYaxis: true,
            },
            animations: {
              enabled: false, // Disabilita animazioni per evitare loop
            },
          },
          theme: {
            mode: "dark",
          },
          colors: ["#4ee088", "#4ee088", "#ff6b6b", "#459095"],
          stroke: {
            curve: "smooth",
            width: [1, 1, 2, 2],
          },
          fill: {
            type: ["gradient", "gradient", "solid", "solid"],
            opacity: [0.07, 0.07, 1, 1],
          },
          markers: {
            size: [0, 0, 3, 3],
            strokeWidth: 0,
            hover: {
              size: [0, 0, 5, 5],
            },
          },
          grid: {
            show: true,
            borderColor: "#444",
            strokeDashArray: 2,
            xaxis: {
              lines: {
                show: true,
              },
            },
            yaxis: {
              lines: {
                show: true,
              },
            },
          },
          xaxis: {
            type: "datetime",
            title: {
              text: "Timestamp",
              style: {
                color: "#fff",
                fontSize: "14px",
              },
            },
            labels: {
              style: {
                colors: "#AAA",
              },
              formatter: function (val) {
                return dayjs(val).format("DD/MM HH:mm");
              },
            },
            axisBorder: {
              color: "#459095",
            },
            axisTicks: {
              color: "#459095",
            },
          },
          yaxis: {
            title: {
              text: "Values",
              style: {
                color: "#fff",
                fontSize: "14px",
              },
            },
            labels: {
              style: {
                colors: "#AAA",
              },
              formatter: function (val) {
                if (val >= 1000000) {
                  return (val / 1000000).toFixed(1) + "M";
                } else if (val >= 1000) {
                  return (val / 1000).toFixed(1) + "K";
                }
                return val.toFixed(1);
              },
            },
            axisBorder: {
              show: true,
              color: "#459095",
            },
          },
          legend: {
            show: true,
            position: "top",
            horizontalAlign: "center",
            labels: {
              colors: "#fff",
            },
            markers: {
              width: 12,
              height: 12,
              radius: 6,
            },
          },
          tooltip: {
            shared: true,
            intersect: false,
            theme: "dark",
            style: {
              fontSize: "12px",
            },
            x: {
              formatter: function (val) {
                return dayjs(val).format("DD/MM/YYYY HH:mm:ss");
              },
            },
            y: {
              formatter: function (
                value,
                { seriesIndex, series, dataPointIndex, w }
              ) {
                // Nasconde completamente le serie "Normal" dal tooltip
                if (seriesIndex === 0 || seriesIndex === 1) {
                  return undefined;
                }

                // Formatta i valori per BPS e PPS
                if (value >= 1000000) {
                  return (value / 1000000).toFixed(1) + "M";
                } else if (value >= 1000) {
                  return (value / 1000).toFixed(1) + "K";
                }
                return value.toFixed(1);
              },
            },
            // Forza la condivisione anche con serie diverse
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
              // Trova il timestamp del punto corrente
              const timestamp = w.globals.seriesX[seriesIndex][dataPointIndex];

              // Costruisci il tooltip personalizzato
              let tooltipContent = `
                <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                  ${dayjs(timestamp).format("DD/MM/YYYY HH:mm:ss")}
                </div>
              `;

              // Aggiungi solo BPS e PPS (serie 2 e 3)
              for (let i = 2; i < series.length; i++) {
                if (series[i] && series[i][dataPointIndex] !== undefined) {
                  const value = series[i][dataPointIndex];
                  let formattedValue;

                  if (value >= 1000000) {
                    formattedValue = (value / 1000000).toFixed(1) + "M";
                  } else if (value >= 1000) {
                    formattedValue = (value / 1000).toFixed(1) + "K";
                  } else {
                    formattedValue = value.toFixed(1);
                  }

                  const seriesName = w.globals.seriesNames[i];
                  const color = w.globals.colors[i];

                  tooltipContent += `
                    <div class="apexcharts-tooltip-series-group apexcharts-active" style="display: flex; align-items: center; padding: 3px 0;">
                      <span class="apexcharts-tooltip-marker" style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; margin-right: 8px;"></span>
                      <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                          <span class="apexcharts-tooltip-text-y-label">${seriesName}: </span>
                          <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
                        </div>
                      </div>
                    </div>
                  `;
                }
              }

              return `<div class="apexcharts-tooltip-box" style="padding: 8px;">${tooltipContent}</div>`;
            },
          },
          dataLabels: {
            enabled: false,
          },
        };

        setChartData({ series, options });
        setCharged(true);
      } catch (error) {
        console.error("[NetworkTrafficChart] Errore nella preparazione dei dati del grafico:", error);
        setCharged(true); // Mostra comunque che il caricamento è terminato
      }
    }
  }, [dataAnomalies, dataNormal]);

  const columns = [
    {
      field: "timeStamp",
      headerName: "TIMESTAMP",
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      align: "center",
    },
    {
      field: "bps",
      headerName: "BPS",
      flex: 1,
      minWidth: 80,
      headerAlign: "left",
      align: "left",
      valueFormatter: (valueR) => {
        const value = Number(valueR);
        return isNaN(value) ? "" : value.toFixed(2);
      },
    },
    {
      field: "pps",
      headerName: "PPS",
      headerAlign: "center",
      flex: 1,
      minWidth: 80,
      align: "center",
      valueFormatter: (valueR) => {
        const value = Number(valueR);
        return isNaN(value) ? "" : value.toFixed(2);
      },
    },
    {
      field: "LOGS",
      headerName: "LOGS",
      sortable: false,
      filterable: false,
      disableExport: true,
      align: "center",
      headerAlign: "center",
      minWidth: 50,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => {
            setFilteredTime(params.row.timeStamp);
            setOpenDialog(true);
          }}
        >
          <DehazeIcon />
        </IconButton>
      ),
    },
  ];

  const columnsDialog = [
    {
      field: "dettaglio",
      headerName: "DETAILS",
      flex: 1,
      minWidth: 280,
      headerAlign: "left",
      align: "left",
      type: "string",
      filterable: true,
      renderCell: (params) => {
        const detail = params.value;
        return (
          <div
            className="detail-clickable-cell"
            style={{ width: "auto", color: TOEstablishTheColorOfLog(detail) }}
          >
            {params.value}
          </div>
        );
      },
    },
    {
      field: "count",
      headerName: "Count",
      headerAlign: "center",
      flex: 1,
      minWidth: 30,
      align: "center",
    },
    {
      field: "tipo",
      headerName: "Type",
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      align: "center",
      renderCell: renderTypeCell,
    },
  ];

  const closeDialog = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <Dialog open={openDialog} onClose={closeDialog} fullWidth>
        <DialogTitle>
          LOGS AROUND {filteredTime}
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DataGridPro
            sx={{
              marginTop: 5,
              fontWeight: 900,
              height: "50vh",
              width: "100%",
              borderRadius: 5,
              border: "solid 3px #459095",
              animation: `${borderColorChange} 3s linear infinite`,
              overflowX: "auto",
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              "& .name-column--cell": {
                color: "#1D7B91",
                fontWeight: 900,
              },
              "& .MuiDataGrid-container--top [role=row]": {
                backgroundColor: "#5E3F82 !important",
              },
              "& .MuiDataGrid-toolbarContainer": {
                borderTopRightRadius: 17,
                borderTopLeftRadius: 17,
                gap: 4,
                backgroundColor: "#5e3f82",
                color: "#1D7B91",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
                backgroundColor: "#5e3f82",
                borderBottomLeftRadius: 20,
                borderBottomRightRadius: 20,
              },
              "& .MuiCheckbox-root": {
                color: "#1D7B91 !important",
              },
              "& .MuiButtonBase-root": {
                color: "#fff !important",
              },
              "& .MuiCircularProgress-root": {
                color: "#5b9ba9 !important",
              },
              "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell":
                {
                  whiteSpace: "normal !important",
                },
              "& .MuiBadge-badge": {
                backgroundColor: "#dd8d16",
              },
              "& .MuiDataGrid-root .MuiDataGrid-container--bottom [role=row]": {
                background: "#D0D0D0 !important",
              },
              "& .MuiDataGrid-panelWrapper": {
                width: "600px !important",
              },
              "& .MuiDataGrid-overlayWrapperInner": {
                backgroundColor: "#1a1a1a !important",
              },
              "& .MuiDataGrid-virtualScrollerContent": {
                backgroundColor: "#1a1a1a !important",
              },
              "& .MuiDataGrid-filler": {
                backgroundColor: "#1a1a1a !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 900,
              },
              "& .MuiDataGrid-virtualScroller": {
                borderRadius: 0,
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#5e3f82",
              },
              "& .MuiDataGrid-toolbar": {
                backgroundColor: "#5e3f82",
              },
            }}
            rows={filteredData}
            columns={columnsDialog}
            loading={loadingFiltered}
          />
        </DialogContent>
      </Dialog>
      <StyledCard>
        <CardContent>
        <Grid container spacing={2}>
          <Grid size={8}>
            <div style={{ marginBottom: "16px" }}>
              <h2
                style={{
                  color: "#fff",
                  textAlign: "center",
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                Network Anomaly Traffic Monitor
              </h2>
              <p
                style={{
                  color: "#AAA",
                  textAlign: "center",
                  margin: "5px 0 0 0",
                  fontSize: "14px",
                }}
              >
                BPS (Bytes per Second) & PPS (Packets per Second)
              </p>
            </div>
            {!charged ? (
              <CircularIndeterminate charged={charged} />
            ) : (
              chartData.series.length > 0 && (
                <ReactApexChart
                  options={chartData.options}
                  series={chartData.series}
                  type="line"
                  height={500}
                />
              )
            )}
          </Grid>
          <Grid size={4}>
            <DataGridPro
              sx={{
                fontWeight: 900,
                height: "70vh",
                width: "100%",
                borderRadius: 5,
                border: "solid 3px #459095",
                animation: `${borderColorChange} 3s linear infinite`,
                overflowX: "auto",
                "& .MuiDataGrid-root": {
                  border: "none",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                },
                "& .name-column--cell": {
                  color: "#1D7B91",
                  fontWeight: 900,
                },
                "& .MuiDataGrid-container--top [role=row]": {
                  backgroundColor: "#5E3F82 !important",
                },
                "& .MuiDataGrid-toolbarContainer": {
                  borderTopRightRadius: 17,
                  borderTopLeftRadius: 17,
                  gap: 4,
                  backgroundColor: "#5e3f82",
                  color: "#1D7B91",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "none",
                  backgroundColor: "#5e3f82",
                  borderBottomLeftRadius: 20,
                  borderBottomRightRadius: 20,
                },
                "& .MuiCheckbox-root": {
                  color: "#1D7B91 !important",
                },
                "& .MuiButtonBase-root": {
                  color: "#fff !important",
                },
                "& .MuiCircularProgress-root": {
                  color: "#5b9ba9 !important",
                },
                "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight) > .MuiDataGrid-cell":
                  {
                    whiteSpace: "normal !important",
                  },
                "& .MuiBadge-badge": {
                  backgroundColor: "#dd8d16",
                },
                "& .MuiDataGrid-root .MuiDataGrid-container--bottom [role=row]":
                  {
                    background: "#D0D0D0 !important",
                  },
                "& .MuiDataGrid-panelWrapper": {
                  width: "600px !important",
                },
                "& .MuiDataGrid-overlayWrapperInner": {
                  backgroundColor: "#1a1a1a !important",
                },
                "& .MuiDataGrid-virtualScrollerContent": {
                  backgroundColor: "#1a1a1a !important",
                },
                "& .MuiDataGrid-filler": {
                  backgroundColor: "#1a1a1a !important",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 900,
                },
                "& .MuiDataGrid-virtualScroller": {
                  borderRadius: 0,
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#5e3f82",
                },
                "& .MuiDataGrid-toolbar": {
                  backgroundColor: "#5e3f82",
                },
              }}
              rows={tableData}
              columns={columns}
              loading={!charged}
            />
          </Grid>
        </Grid>
                </CardContent>
      </StyledCard>
    </>
  );
};

export default NetworkTrafficChart;
